/**
 * floatingLayout.js — Iter 346 (Feb 2026)
 *
 * Single source of truth for the bottom-right "floating widget lane".
 *
 * The CRM has four independently-mountable floating widgets that all
 * historically anchored to the bottom-right corner without any
 * coordination — the shared bottom-dock pill (AI-FAB + NotificationBell
 * + RingCentral pill), the Live Notes NoteTaker, the FloatingLiveCallCoach
 * (AI Assist), and the expanded RingCentral softphone panel. Because each
 * chose its own corner offset, three of them physically overlapped during
 * an active call.
 *
 * This module fixes that at the source: every floating widget imports
 * these constants and either
 *   (a) mounts inside the shared bottom-dock in App.js, OR
 *   (b) clamps its saved drag position so it can never land inside the
 *       reserved bottom-dock strip.
 *
 * Layout diagram (measured from viewport bottom-right):
 *
 *   ┌────────────────────────────────────────────────────────┐
 *   │ FloatingLiveCallCoach (draggable, y >= FLOATER_MIN)    │
 *   │                                                        │
 *   ├── Panels anchored here open UPWARD (bottom: 72) ───────┤
 *   ├── DOCK_HEIGHT (44px)  bottom: 16   right: 16 ──────────┤
 *   │  [ AI-FAB ][ 🔔 ][ ✎ Notes ][ 📞 RC pill ]              │
 *   └────────────────────────────────────────────────────────┘
 */

// Distance from the viewport bottom to the bottom of the dock pill.
export const DOCK_BOTTOM = 16;

// Vertical footprint of the dock pill (button height + inner padding).
// The dock in App.js uses 36px buttons + 6px padding on each side = 48px
// total. Keep this a hair generous so panels that anchor above the dock
// have a visible gap.
export const DOCK_HEIGHT = 48;

// Gap between the top of the dock and any panel that anchors above it.
export const LANE_GAP = 8;

// Bottom offset panels use when they open UPWARD from the dock.
// Live Notes panel, AI chat panel expansions, etc.
export const PANEL_ANCHOR_BOTTOM = DOCK_BOTTOM + DOCK_HEIGHT + LANE_GAP; // 72

// Minimum `bottom` any draggable floating widget is allowed to occupy.
// If a user's saved drag position falls below this, we clamp it silently
// on load. Prevents the "dropped it under the dock and can't see it"
// class of bugs.
export const FLOATER_MIN_BOTTOM = PANEL_ANCHOR_BOTTOM; // 72

// Minimum `right` any draggable widget is allowed to occupy — mirrors
// the dock's 16px inset so nothing hugs the extreme edge of the viewport.
export const FLOATER_MIN_RIGHT = 16;

// Standard width of panels that anchor into the bottom-right lane.
export const LANE_WIDTH = 380;

// Z-index band. The dock itself sits at 9700 (hard-coded in App.js — do
// NOT raise below that or the dock hides behind the panel).
export const Z_DOCK = 9700;
export const Z_DOCK_PANEL = 9600;     // Panels that anchor above the dock.
export const Z_FLOATER = 9100;        // Draggable widgets (AI Assist, RC expanded).

/**
 * Clamp a saved drag position so it can't land inside the reserved bottom
 * dock strip or off the right edge. Returns a new object (does not mutate).
 *
 * @param {{ right: number, bottom: number } | null | undefined} pos
 * @returns {{ right: number, bottom: number }}
 */
export function clampFloaterPos(pos) {
  const r = Number(pos?.right);
  const b = Number(pos?.bottom);
  return {
    right: Number.isFinite(r) ? Math.max(FLOATER_MIN_RIGHT, r) : FLOATER_MIN_RIGHT + 8,
    bottom: Number.isFinite(b) ? Math.max(FLOATER_MIN_BOTTOM, b) : FLOATER_MIN_BOTTOM + 8,
  };
}

/**
 * Sensible default position for any draggable widget that has no saved
 * position. Sits just above the reserved dock strip, one small offset
 * from the right edge.
 */
export function defaultFloaterPos() {
  return { right: FLOATER_MIN_RIGHT + 8, bottom: FLOATER_MIN_BOTTOM + 8 };
}
